import React, { useEffect, useState, useContext, useMemo } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Link } from "react-router-dom"
import get from "lodash.get"

import SidebarItems from "../SidebarItems"
import SignOutButton from "../SignOut"
import Logo from "../Logo"

import { AuthUserContext } from "../../../context/Session"
import { withFirebase } from "../../../utils/Firebase"

// Helpers
const normalizePath = (path) => (path?.startsWith("/") ? path : `/${path}`)

const getVisibleItems = (
  items,
  userRoles,
  hasMessagingAccess,
  isFooter = false
) =>
  items.filter((item) => {
    if (!!item.isFooter !== isFooter) return false

    const hasValidRole =
      item.rolesAllowed?.includes("ANY") ||
      item.rolesAllowed?.some((role) => userRoles[role])

    const messagingCheck = item.requiresMessagingAccess
      ? hasMessagingAccess
      : true

    return hasValidRole && messagingCheck
  })

const renderSidebarLink = (item, index, activeIndex) => {
  const IconItem = item.icon
  return (
    <Link
      to={item.route}
      key={`sb-${item.key}`}
      aria-current={index === activeIndex ? "page" : undefined}
    >
      <SidebarItem active={index === activeIndex}>
        <IconItem size="24" />
        {item.bubble && (
          <Bubble>
            <span>+99</span>
          </Bubble>
        )}
      </SidebarItem>
    </Link>
  )
}

// Main Component
function Sidebar({ history: { location }, defaultActive, firebase }) {
  const authUser = useContext(AuthUserContext)
  const lastActiveIndex = Number(localStorage.getItem("lastActiveIndex") ?? NaN)
  const [currentOrg, setCurrentOrg] = useState(null)

  const [activeIndex, setActiveIndex] = useState(
    Number.isNaN(lastActiveIndex) ? defaultActive : lastActiveIndex
  )

  const userRoles = useMemo(() => get(authUser, "roles", {}), [authUser])
  const hasMessagingAccess = useMemo(
    () => get(currentOrg, "hasMessagingAccess", false),
    [currentOrg]
  )

  const visibleItems = useMemo(
    () => getVisibleItems(SidebarItems, userRoles, hasMessagingAccess),
    [userRoles, hasMessagingAccess]
  )

  const visibleFooterItems = useMemo(
    () => getVisibleItems(SidebarItems, userRoles, hasMessagingAccess, true),
    [userRoles, hasMessagingAccess]
  )

  useEffect(() => {
    let cancelled = false

    const getOrg = async () => {
      if (!authUser?.organizationId || !firebase) return

      const org = await firebase.org(authUser.organizationId).get()
      if (!cancelled && org?.exists) {
        const orgData = org.data()
        const messagingAccessChanged =
          currentOrg?.hasMessagingAccess !== orgData?.hasMessagingAccess

        if (!currentOrg || messagingAccessChanged) {
          setCurrentOrg(orgData)

          if (orgData?.hasMessagingAccess && !currentOrg?.hasMessagingAccess) {
            await firebase.updateMessagingAccessForOrgUsers({
              organizationId: authUser.organizationId,
              access: true,
            })

            await firebase.refreshAuthUser()
          }
        }
      }
    }

    getOrg()

    return () => {
      cancelled = true
    }
  }, [authUser?.organizationId, firebase, currentOrg])

  useEffect(() => {
    const activeItem = visibleItems.findIndex(
      (item) => normalizePath(item.route) === normalizePath(location.pathname)
    )
    setActiveIndex(activeItem)
    localStorage.setItem("lastActiveIndex", activeItem)
  }, [location, visibleItems])

  return (
    <SidebarParent>
      <FixedSidebar>
        <LogoContainer>
          <Logo />
        </LogoContainer>

        {visibleItems.map((item, index) =>
          renderSidebarLink(item, index, activeIndex)
        )}

        <FooterContainer>
          {visibleFooterItems.map((item, index) =>
            renderSidebarLink(item, index, activeIndex)
          )}
          <LoggedUser>
            <span>DOC</span>
          </LoggedUser>
          <SignOutButton />
        </FooterContainer>
      </FixedSidebar>
      <div className="behind-the-scenes" />
    </SidebarParent>
  )
}

Sidebar.propTypes = {
  history: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    location: PropTypes.object.isRequired,
  }).isRequired,
  defaultActive: PropTypes.number,
  firebase: PropTypes.shape().isRequired,
}

Sidebar.defaultProps = {
  defaultActive: 0,
}

export default withFirebase(Sidebar)

// Styles
const LogoContainer = styled.div`
  padding: 30px 12.5px;
  margin-bottom: 14px;
`

const SidebarParent = styled.div`
  background: #fff;
  border: 1px solid #f2f2f2;
  position: relative;
  flex-shrink: 0;
  width: 71px;

  a {
    text-decoration: none;
  }

  .behind-the-scenes {
    width: 71px;
  }
`

const FixedSidebar = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  background: #fff;
  border-right: 1px solid #f2f2f2;
  width: 71px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`

const SidebarItem = styled.div`
  position: relative;
  transition: all 0.25s ease-in-out;
  color: ${(props) =>
    props.active ? props.theme.color.primary : props.theme.color.black.normal};
  margin-bottom: 35px;
  border-radius: 4px;

  &:hover {
    cursor: pointer;
    background: #c34a36;
  }
`

const FooterContainer = styled.div`
  height: auto;
  position: absolute;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const LoggedUser = styled.div`
  width: 40px;
  height: 40px;
  margin-bottom: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #f2f2f2;
  box-sizing: border-box;
  border-radius: 100px;
`

const Bubble = styled.div`
  position: absolute;
  width: 28px;
  height: 24px;
  top: -15px;
  left: 10px;
  background-color: ${({ theme }) => theme.color.error};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 0px 0px 8px;
  border-radius: 100px;

  & > span {
    font-size: 0.75rem;
    line-height: 1rem;
    letter-spacing: 0.025rem;
    color: ${({ theme }) => theme.color.white.normal};
  }
`
